import {
  setUser as setSentryUser,
  addBreadcrumb as addSentryBreadcrumb,
  setContext as setSentryContext,
  setTag as setSentryTag,
} from '@sentry/react';
import { instance as datadogInstance, getLogger } from './instance';

interface DatadogUser {
  id: string;
  email: string;
  name: string;
}

interface DatadogExperiment {
  id: number;
  variant: number;
}

export function log(message: string, data: Record<string, unknown>) {
  getLogger().log(message, { data });

  if (process.env.PUBLIC_ENABLE_CONSOLE_LOGS === 'true') {
    // eslint-disable-next-line no-console -- allow console logging
    console.log(message, { data });
  }

  addSentryBreadcrumb({
    message,
    data,
    level: 'info',
  });
}

/**
 * Logs unhandled API error responses to identify them so we can add the necessary error handling iteratively.
 *
 * @param error - The error message or error object
 * @param data - Additional data to log. This should include `key` so we can easily trace the log back to a line of code
 *
 * @example
 * logUnknownErrorResponse('Error message', { key: 'universally.unique.period.separated.string', anyKey: value });
 */
export function logUnknownErrorResponse(
  error: unknown,
  data: Record<string, unknown> = {}
) {
  const stringError =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : 'Unknown error';

  if (process.env.PUBLIC_HR_ENV !== 'production') {
    // eslint-disable-next-line no-console -- allow console logging in non-prod envs for debugging
    console.error(stringError, data);
  }

  getLogger().log('Unknown error response', {
    attention_required: true,
    error: stringError,
    data,
  });

  addSentryBreadcrumb({
    message: stringError,
    data,
    level: 'error',
  });
}

export function logAction<D extends object | undefined>(name: string, data: D) {
  const instance = datadogInstance();
  if (instance === null) {
    return;
  }
  instance.addAction(name, data);
}

export function setUser(user: DatadogUser) {
  getLogger().setContextProperty('user', user);

  // attach user to datadog rum
  const instance = datadogInstance();
  if (instance === null) {
    return;
  }
  const currentUser = instance.getUser();
  if (currentUser === undefined || currentUser.id !== user.id) {
    const { id, email, name } = user;
    logAction('Setting user', {
      id,
      email,
      name,
    });
    instance.setUser({
      id,
      email,
      name,
    });

    setSentryUser({
      id,
    });
  }
}

export function setExperiments(experiments: DatadogExperiment[]) {
  const experimentsMap = experiments.reduce(
    (seed, experiments) => {
      seed[experiments.id] = experiments.variant.toString();
      return seed;
    },
    {} as Record<string, string>
  );

  getLogger().setContextProperty('experiments', experimentsMap);
  setSentryContext('experiments', experimentsMap);

  // Trying out experiment tags to allow filtering by experiments in Sentry.
  // Add experiments as needed as adding too many can decrease performance.
  setSentryTag('exp_171', experimentsMap[17100]);

  // attach experiments to datadog rum
  const instance = datadogInstance();
  if (instance === null) {
    return;
  }

  logAction('Setting experiments', experimentsMap);

  instance.setUserProperty('experiments', experimentsMap);
}
